import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Tooltip } from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import { Pencil, Save } from "lucide-react";

import messages from "../../../../assets/locale/messages";
import TruncatedWithTooltip from "../../../TruncatedWithTooltip";
import { UIButton, UIPageHeader } from "../../../ui";
import { clx, formatDateTime } from "../../../../utils/Helpers";

import "./_CompanyHeader.scss";

const statusChip = (company, registrationReq, companiesList) => {
  const s = (company?.status || "").toLowerCase();
  const deactivated =
    s === "deactivated" ||
    s === "inactive" ||
    company?.is_active === false ||
    company?.active === false;

  if (deactivated) {
    return {
      label: companiesList.deactivated,
      status: "deactivated",
    };
  }
  if (s === "rejected") {
    return {
      label: registrationReq.statusRejected,
      status: "rejected",
    };
  }
  if (s === "pending") {
    return {
      label: registrationReq.statusPendingApproval,
      status: "pending",
    };
  }
  return {
    label: companiesList.active,
    status: "active",
  };
};

const CompanyHeader = ({
  company,
  onApprove,
  onRejectClick,
  hasJoinedDate,
  onEditProfile,
  isEditMode,
  onCancelEdit,
  onSaveEdit,
  hasEditProfileButton,
  editProfileDisabled,
  editProfileDisabledTooltip,
  saveDisabled,
  saveLoading,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { registrationReq, companiesList, shared } = messages[lang];

  if (!company) return null;

  const chip = statusChip(company, registrationReq, companiesList);
  const imageUrl =
    company.image_url != null && String(company.image_url).trim()
      ? String(company.image_url).trim()
      : null;
  const headerAvatar = imageUrl
    ? { image: imageUrl, name: company.name_en || "", size: "lg" }
    : null;
  const headerIcon = !imageUrl ? <BusinessIcon /> : null;
  const headerClassName = clx(
    "company-page-header",
    isEditMode && "company-page-header--with-edit-actions",
  );
  const headerSubtitle = [
    <TruncatedWithTooltip key="company-name-ar" title={company.name_ar || "—"} lines={2}>
      <span>{company.name_ar || "—"}</span>
    </TruncatedWithTooltip>,
    hasJoinedDate ? (
      <span key="joined-date">
        {shared.joined} {formatDateTime(company.created_at, lang)}
      </span>
    ) : null,
  ].filter(Boolean);

  const handleEditProfile = () => {
    onEditProfile();
  };

  const headerActions = (
    <>
      {company.status === "pending" && (
        <div className="company-view-header-actions">
          <UIButton
            type="button"
            variant="outlined"
            color="inherit"
            size="large"
            onClick={onRejectClick}
          >
            {registrationReq.rejectRegistration}
          </UIButton>
          <UIButton
            type="button"
            variant="success"
            size="large"
            onClick={onApprove}
          >
            {registrationReq.approveRegistration}
          </UIButton>
        </div>
      )}

      {!isEditMode && hasEditProfileButton && (
        <Tooltip
          title={
            editProfileDisabled && editProfileDisabledTooltip
              ? editProfileDisabledTooltip
              : ""
          }
          placement="top"
          arrow
          disableHoverListener={!editProfileDisabled}
          disableFocusListener={!editProfileDisabled}
        >
          <span>
            <UIButton
              type="button"
              appearance="gradient"
              size="large"
              startIcon={<Pencil size={16} />}
              onClick={handleEditProfile}
              disabled={editProfileDisabled}
            >
              {shared.editProfile}
            </UIButton>
          </span>
        </Tooltip>
      )}

      {isEditMode && (
        <div className="company-page-header__edit-actions">
          <UIButton
            type="button"
            variant="outline"
            color="inherit"
            size="large"
            className="company-page-header__cancel-btn"
            onClick={onCancelEdit}
            disabled={saveLoading}
          >
            {shared.cancel}
          </UIButton>
          <UIButton
            type="button"
            appearance="gradient"
            size="large"
            startIcon={<Save size={16} />}
            onClick={onSaveEdit}
            disabled={saveDisabled}
            loading={saveLoading}
          >
            {companiesList.saveChanges}
          </UIButton>
        </div>
      )}
    </>
  );

  return (
    <UIPageHeader
      actions={headerActions}
      avatar={headerAvatar}
      icon={headerIcon}
      iconStatus={chip.status}
      className={headerClassName}
      status={chip.status}
      statusLabel={chip.label}
      subtitle={headerSubtitle}
      title={(
        <TruncatedWithTooltip title={company.name_en || "—"} lines={2}>
          <span>{company.name_en || "—"}</span>
        </TruncatedWithTooltip>
      )}
    />
  );
};

CompanyHeader.propTypes = {
  company: PropTypes.object.isRequired,
  onApprove: PropTypes.func,
  onRejectClick: PropTypes.func,
  hasJoinedDate: PropTypes.bool,
  onEditProfile: PropTypes.func,
  isEditMode: PropTypes.bool,
  onCancelEdit: PropTypes.func,
  onSaveEdit: PropTypes.func,
  hasEditProfileButton: PropTypes.bool,
  editProfileDisabled: PropTypes.bool,
  editProfileDisabledTooltip: PropTypes.string,
  saveDisabled: PropTypes.bool,
  saveLoading: PropTypes.bool,
};

CompanyHeader.defaultProps = {
  onApprove: () => {},
  onRejectClick: () => {},
  hasJoinedDate: false,
  onEditProfile: () => {},
  isEditMode: false,
  onCancelEdit: () => {},
  onSaveEdit: () => {},
  hasEditProfileButton: true,
  editProfileDisabled: false,
  editProfileDisabledTooltip: "",
  saveDisabled: false,
  saveLoading: false,
};

export default CompanyHeader;
